import React from 'react';
import { useForm } from 'react-hook-form';

import styled from '@emotion/styled';

import { Input, Button, Box, Label } from '@clutter/clean';

import { Alert } from '@shared/components/bootstrap';
import { valid } from '@shared/config/password';

import { Logo } from '@portal/components/shared/logo';
import { Settings__UpdatePasswordInput, Status, useUpdatePasswordMutation } from '@portal/schema';

import { PasswordMatches } from './matches';
import { PasswordStrength } from './strength';

const Field: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box.Flex flexDirection="column" gap="2px">
    {children}
  </Box.Flex>
);

export const RotatePassword: React.FC = () => {
  const [save, { loading }] = useUpdatePasswordMutation();
  const { register, handleSubmit, watch, reset } = useForm<Settings__UpdatePasswordInput>();
  const password = watch('password') ?? '';
  const passwordConfirmation = watch('passwordConfirmation') ?? '';
  const disabled = !valid(password);

  const onSubmit = async (input: Settings__UpdatePasswordInput) => {
    if (disabled) return;
    const response = await save({ variables: { input } });
    const status = response.data?.updatePassword?.status;

    if (status === Status.Ok) location.replace('/restore');
  };

  return (
    <Box margin="auto" maxWidth="640px" minWidth="320px">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box.Flex flexDirection="column" gap="24px">
          <Box.Flex justifyContent="center">
            <Logo />
          </Box.Flex>

          <Alert style="info">
            To ensure the security of your account and data, please create a new password that meets our enhanced
            security guidelines.
          </Alert>

          <Box.Flex flexDirection="column" gap="16px">
            <Field>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                required
                ref={register()}
                type="password"
                id="currentPassword"
                name="currentPassword"
                autoComplete="current-password"
              />
            </Field>
            <Field>
              <Label htmlFor="password">New Password</Label>
              <Input
                required
                ref={register()}
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
              />
              <PasswordStrength password={password} />
            </Field>
            <Field>
              <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
              <Input
                ref={register()}
                type="password"
                id="passwordConfirmation"
                name="passwordConfirmation"
                autoComplete="new-password"
              />
              <PasswordMatches password={password} confirmation={passwordConfirmation} />
            </Field>
          </Box.Flex>

          <Button kind="primary" size="medium" loading={loading} disabled={disabled} type="submit">
            Change Password
          </Button>
        </Box.Flex>
      </form>
    </Box>
  );
};
